/**
 * Cart drawer bubbles
 * -------------------------------------------------------------------------------------------------
 * Cross-sell ("+ ADD") and upsell ("UPGRADE") offers shown as thought-bubbles next to a line item in
 * the cart drawer. The markup comes from snippets/cart-bubble.liquid, and sections/mini-cart.liquid
 * decides WHICH lines get one. This file adds the behaviour:
 *
 *  - draws the scalloped cloud outline to the bubble's real size (CSS paints a plain rounded
 *    fallback until then, so a bubble is never broken when this file is late or missing),
 *  - reveals a bubble with a staggered "thought" animation the first time it is seen, and renders
 *    it still on every later re-render of the drawer,
 *  - "+ ADD" adds the offered product,
 *  - "UPGRADE" adds the offered product and only THEN removes the line it replaces, so a failed add
 *    (sold out, stock limit) never costs the customer the item they had already chosen,
 *  - "Not now" hides that bubble until the next add to cart (or a reload / the next page).
 *
 * The drawer re-renders by replacing its innerHTML (theme.js, CartDrawer._rerenderCart), so after
 * every cart change each bubble is a brand-new element. Anything that must survive that lives here
 * at module level, keyed by the bubble's data attributes.
 *
 * Cart updates follow the theme's own contract (see box-builder.js refreshCart): cart:updated with
 * the cart JSON, then cart:refresh with the pre-rendered "mini-cart" section. That keeps the cart
 * count, freebie sync (theme.js syncFreebies), progress bar and box builder in step. quickbuy:upsell
 * is deliberately NOT dispatched: it would open the product page's "Pairs well with" modal.
 */
(() => {
  if (window.customElements.get('cart-bubble')) {
    return;
  }

  const DISMISSED_STORAGE_KEY = 'a2g-cart-bubble-dismissed';
  const SECTION_ID = 'mini-cart'; // CartDrawer reads sections['mini-cart'] by this exact name
  const GENERIC_ERROR = "We couldn't update your cart. Please try again.";
  // Line item property naming the bubble an item was added from (the "order label" settings). The
  // leading underscore hides it in the cart, cart page and checkout; it still shows on the order.
  const ORDER_PROPERTY = '_Added via';

  // Motion is "playful & bouncy" and plays only right after an add to cart (see armEntrance).
  const OPEN_REVEAL_DELAY = 780; // the drawer slides in and fades its line items in first (theme.js)
  const LIVE_REVEAL_DELAY = 180; // a bubble that appears while the drawer is already open
  const REVEAL_STAGGER = 220; // between bubbles on different lines
  const DOT_STAGGER = 95;
  const CONTENT_STAGGER = 65;
  const ENTRANCE_WINDOW = 8000; // how long after an add a newly rendered bubble still plays its entrance
  const NUDGE_FIRST = 2600; // idle: first wiggle of the button after the entrance settles
  const NUDGE_EVERY = 5200;
  const NUDGE_TIMES = 3; // then it stops: a nudge that never ends becomes noise

  // Spring-ish easings. Overshoot lives in the keyframes; these just keep each segment smooth.
  const EASE_POP = 'cubic-bezier(0.34, 1.56, 0.64, 1)';
  const EASE_SETTLE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const EASE_IN = 'cubic-bezier(0.55, 0, 0.7, 0.2)';

  const watchedDrawers = new WeakSet();

  /** Button nudges already played, per bubble key: re-renders replace elements, the count survives. */
  const nudgeCounts = new Map();

  /**
   * The entrance plays only when the drawer is showing because something was just added to the cart.
   * Any other render (opening the drawer from the cart icon, a quantity change, a freebie sync) shows
   * the bubbles already in place.
   *
   *   all: true   added from outside the drawer (product page, quick buy, box builder, upsell modal):
   *               every bubble in the drawer plays its entrance once.
   *   all: false  added from inside the drawer (a bubble's "+ ADD", the "Complete your Stack" cards):
   *               only bubbles that weren't on screen already pop in; the rest stay put.
   *
   * `played` keeps a bubble from replaying when the drawer re-renders again for the same add (the
   * freebie sync does that), since every re-render creates brand-new bubble elements.
   */
  let entrance = null;

  function visibleBubbleKeys() {
    return new Set(Array.from(document.querySelectorAll('cart-drawer cart-bubble'), (bubble) => bubble.dataset.key));
  }

  function armEntrance(all) {
    const now = Date.now();

    // A new add to cart is a new moment: offers the customer said "Not now" to are shown again.
    // Removed bubbles aren't on screen, so they count as new and play their entrance.
    dismissedKeys.clear();

    if (entrance && entrance.until > now) {
      // Already armed for this add: widen to "all" if needed, and give slow networks more time.
      entrance.all = entrance.all || all;
      entrance.until = now + ENTRANCE_WINDOW;
      return;
    }

    entrance = { all, until: now + ENTRANCE_WINDOW, before: visibleBubbleKeys(), played: new Set() };
  }

  function wantsEntrance(key) {
    if (isDesignMode()) {
      return true; // the theme editor has no add to cart: let merchants preview the motion
    }

    if (!entrance || entrance.until < Date.now() || entrance.played.has(key)) {
      return false;
    }

    if (!entrance.all && entrance.before.has(key)) {
      return false;
    }

    entrance.played.add(key);
    return true;
  }

  // Adds from product forms (theme.js ProductForm), the drawer's recommendation cards, and bubbles.
  document.addEventListener('variant:added', (event) => {
    const fromDrawer = Boolean(event.detail && event.detail.cartBubble) ||
      (event.target instanceof Element && Boolean(event.target.closest('cart-drawer')));

    armEntrance(!fromDrawer);
  });

  // Adds that don't announce variant:added but do open the drawer (box builder, upsell modal).
  document.documentElement.addEventListener('cart:refresh', (event) => {
    if (event.detail && event.detail.openMiniCart) {
      armEntrance(true);
    } else if (entrance && entrance.until > Date.now()) {
      entrance.until = Date.now() + ENTRANCE_WINDOW; // a follow-up refresh for the same add
    }
  });

  /** Variant just added from a bubble; its new line gets an entrance once the drawer re-renders. */
  let variantToHighlight = null;
  let highlightTimer = null;
  /** Set when that line arrived through an upgrade: it gets the arrow-up celebration too. */
  let lineCelebration = null;

  /**
   * The celebration on screen: { variantId, tag, burst, until }. An upgrade is usually followed by
   * more re-renders for the same moment (the freebie sync adds unlocked gifts about a second later),
   * and each one replaces the line. The "Upgraded" pill and the still-animating badge are moved onto
   * the new line, so the celebration carries on instead of vanishing. Ends when the drawer closes.
   */
  let upgradeMoment = null;
  const UPGRADE_MOMENT_WINDOW = 10000;

  const SVG_NS = 'http://www.w3.org/2000/svg';
  const ARROW_UP_PATH = 'M20 27V13M14 19l6-6 6 6';

  function svg(markup, viewBox) {
    const element = document.createElementNS(SVG_NS, 'svg');
    element.setAttribute('viewBox', viewBox);
    element.setAttribute('aria-hidden', 'true');
    element.setAttribute('focusable', 'false');
    element.innerHTML = markup;
    return element;
  }

  /**
   * Brings a line into view by scrolling ONLY the drawer's own list (.drawer__content).
   * Not scrollIntoView(): that scrolls every scrollable ancestor, the page included. Safari does so
   * even for an element inside the fixed drawer, which made the product page behind it jump.
   */
  function revealInDrawer(line, animate) {
    const scroller = line.closest('.drawer__content');

    if (!scroller) {
      return;
    }

    const box = scroller.getBoundingClientRect();
    const rect = line.getBoundingClientRect();
    let delta = 0;

    if (rect.top < box.top) {
      delta = rect.top - box.top - 12; // above the visible part of the list
    } else if (rect.bottom > box.bottom) {
      delta = Math.min(rect.bottom - box.bottom + 12, rect.top - box.top - 12); // below it
    }

    if (Math.abs(delta) > 1) {
      scroller.scrollTo({ top: scroller.scrollTop + delta, behavior: animate ? 'smooth' : 'auto' });
    }
  }

  /**
   * The upgrade moment, on the line the upsell was just swapped for:
   *  - an "Upgraded" pill before the product title (announced to screen readers); it stays through
   *    the follow-up re-renders of the same moment (see upgradeMoment) until the drawer closes,
   *  - a yellow arrow badge on the product image whose arrow shoots up three times inside a sparkle
   *    burst, then the badge pops away (motion only when animation is on),
   *  - the line is scrolled into view, since the new product lands at the top of the cart.
   *
   * Returns { tag, burst } (either may be null) for upgradeMoment.
   */
  function celebrateUpgrade(line, { text, animate }) {
    const meta = line.querySelector('.product-item-meta');
    let tag = null;

    if (meta && !meta.querySelector('.cart-upgrade-tag')) {
      tag = document.createElement('span');
      tag.className = 'cart-upgrade-tag';
      tag.setAttribute('role', 'status');
      tag.append(svg(`<path d="${ARROW_UP_PATH}" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>`, '8 8 24 24'), text);
      meta.prepend(tag);

      if (animate) {
        tag.animate(
          [
            { opacity: 0, transform: 'translateY(6px) scale(0.6)' },
            { opacity: 1, transform: 'translateY(-2px) scale(1.12)', offset: 0.55 },
            { opacity: 1, transform: 'none' },
          ],
          { duration: 520, delay: 380, easing: EASE_SETTLE, fill: 'backwards' }
        );
      }
    }

    revealInDrawer(line, animate);

    const holder = line.querySelector('.line-item__image-wrapper');

    if (!animate || !holder) {
      return { tag, burst: null };
    }

    // Sparks start behind the badge and fly out past its edge (the disc's radius is 17); speed lines
    // trail under the arrow.
    const sparks = [0, 60, 120, 180, 240, 300]
      .map((angle) => {
        const radians = ((angle - 90) * Math.PI) / 180;
        return `<circle class="cart-upgrade-burst__spark" data-dx="${Math.cos(radians) * 25}" data-dy="${Math.sin(radians) * 25}" cx="20" cy="20" r="2.4"/>`;
      })
      .join('');

    const burst = document.createElement('span');
    burst.className = 'cart-upgrade-burst';
    burst.setAttribute('aria-hidden', 'true');
    burst.append(
      svg(
        `${sparks}<circle class="cart-upgrade-burst__disc" cx="20" cy="20" r="17"/>` +
          '<g class="cart-upgrade-burst__lift">' +
          '<path class="cart-upgrade-burst__speed" d="M14 31v-3M26 31v-3"/>' +
          `<path class="cart-upgrade-burst__arrow" d="${ARROW_UP_PATH}"/></g>`,
        '0 0 40 40'
      )
    );
    holder.append(burst);

    const start = 420; // after the line has dropped in
    const disc = burst.querySelector('.cart-upgrade-burst__disc');
    const lift = burst.querySelector('.cart-upgrade-burst__lift');
    const animations = [];

    // The badge pops in with an overshoot.
    animations.push(
      disc.animate(
        [
          { opacity: 0, transform: 'scale(0)' },
          { opacity: 1, transform: 'scale(1.25)', offset: 0.5 },
          { opacity: 1, transform: 'scale(0.92)', offset: 0.75 },
          { opacity: 1, transform: 'none' },
        ],
        { duration: 480, delay: start, easing: EASE_SETTLE, fill: 'backwards' }
      )
    );

    // The arrow (with its speed lines) shoots up out of the badge and back in, three times.
    animations.push(
      lift.animate(
        [
          { opacity: 0, transform: 'translateY(10px)' },
          { opacity: 1, transform: 'translateY(0)', offset: 0.3 },
          { opacity: 1, transform: 'translateY(-3px)', offset: 0.55 },
          { opacity: 0, transform: 'translateY(-14px)', offset: 0.85 },
          { opacity: 0, transform: 'translateY(-14px)' },
        ],
        { duration: 620, delay: start + 160, iterations: 3, easing: 'cubic-bezier(0.3, 0.7, 0.4, 1)', fill: 'backwards' }
      )
    );

    // ...then lands for good in the middle of the badge.
    animations.push(
      lift.animate(
        [
          { opacity: 0, transform: 'translateY(10px)' },
          { opacity: 1, transform: 'translateY(-2px)', offset: 0.6 },
          { opacity: 1, transform: 'none' },
        ],
        // 'forwards', not 'both': holding the first (invisible) keyframe through its delay would
        // override, and hide, the three launches before it.
        { duration: 420, delay: start + 160 + 3 * 620, easing: EASE_SETTLE, fill: 'forwards' }
      )
    );

    // Sparks burst outwards on the first launch.
    burst.querySelectorAll('.cart-upgrade-burst__spark').forEach((spark, index) => {
      const dx = parseFloat(spark.dataset.dx);
      const dy = parseFloat(spark.dataset.dy);

      animations.push(
        spark.animate(
          [
            { opacity: 0, transform: 'translate(0, 0) scale(0.4)' },
            { opacity: 1, transform: `translate(${dx * 0.8}px, ${dy * 0.8}px) scale(1.1)`, offset: 0.45 },
            { opacity: 0, transform: `translate(${dx * 1.35}px, ${dy * 1.35}px) scale(0.3)` },
          ],
          { duration: 760, delay: start + 200 + (index % 2) * 60, easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)', fill: 'both' }
        )
      );
    });

    // After a short hold the whole badge pops away; the "Upgraded" pill stays.
    const exit = burst.animate(
      [
        { opacity: 1, transform: 'none' },
        { opacity: 1, transform: 'scale(1.15)', offset: 0.35 },
        { opacity: 0, transform: 'scale(0) rotate(-20deg)' },
      ],
      { duration: 380, delay: start + 160 + 3 * 620 + 420 + 900, easing: EASE_IN, fill: 'forwards' }
    );

    settle([exit, ...animations], 6000).then(() => {
      burst.remove();
      if (upgradeMoment && upgradeMoment.burst === burst) {
        upgradeMoment.burst = null;
      }
    });

    return { tag, burst };
  }

  /**
   * A later re-render in the same upgrade moment replaced the celebrated line: move the pill and the
   * running badge onto the new one. Script animations keep running while their element is moved.
   */
  function carryUpgradeMoment(drawer) {
    if (!upgradeMoment) {
      return;
    }

    if (upgradeMoment.until < Date.now()) {
      upgradeMoment = null;
      return;
    }

    const line = drawer.querySelector(`line-item[data-id="${upgradeMoment.variantId}"]`);

    if (!line) {
      return;
    }

    const { tag, burst } = upgradeMoment;
    const meta = line.querySelector('.product-item-meta');
    const holder = line.querySelector('.line-item__image-wrapper');

    if (tag && meta && !meta.contains(tag)) {
      meta.querySelector('.cart-upgrade-tag')?.remove();
      meta.prepend(tag);
    }

    if (burst && holder && !holder.contains(burst)) {
      holder.append(burst);
    }
  }

  /**
   * Waits for animations, but never longer than `limit` ms. Animation timelines stop advancing when
   * no frames are produced (background tab, throttled renderer), and the cart refresh waits on
   * these; it must never hang on a promise that may not settle.
   */
  function settle(animations, limit) {
    const finished = Promise.all(animations.map((animation) => animation.finished.catch(() => {})));
    return Promise.race([finished, new Promise((resolve) => setTimeout(resolve, limit))]);
  }

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);
  const routes = () => (window.themeVariables && window.themeVariables.routes) || {};
  const rootUrl = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ------------------------------------------------------------------------------------------------
   * Dismissals ("Not now"). Kept in memory only until the next add to cart: the customer declined the
   * offer in front of them, not for the whole visit. It stays gone while the drawer re-renders for
   * other reasons (quantity changes, freebie sync), and is offered again on the next add to cart
   * (armEntrance clears it), after a reload, or on the next page.
   * ---------------------------------------------------------------------------------------------- */

  const dismissedKeys = new Set();

  // Earlier versions remembered dismissals for the whole browser session; drop what they saved.
  try {
    window.sessionStorage.removeItem(DISMISSED_STORAGE_KEY);
  } catch (error) {
    // Storage disabled: nothing was saved there either.
  }

  /* ------------------------------------------------------------------------------------------------
   * Cloud outline
   *
   * A superellipse (a rounded rectangle that stays rounded at any aspect ratio) is walked at equal
   * arc-length steps, and each step is joined with an outward arc. The result is a scalloped cloud
   * whose bumps stay the same size however wide or tall the bubble is, which a stretched SVG can't do.
   * ---------------------------------------------------------------------------------------------- */

  const CLOUD_STROKE = 1.25;
  const CLOUD_EXPONENT = 6; // 2 = ellipse; higher = closer to a rectangle, keeping the corners usable
  const CLOUD_BUMP_SPACING = 34;
  const CLOUD_BUMP_RHYTHM = [1, 0.8, 1.12, 0.9, 1.05, 0.85]; // uneven bumps read as a cloud, not a stamp
  const CLOUD_SAMPLES = 480;

  const round = (value) => Math.round(value * 100) / 100;

  function cloudPath(width, height) {
    const bump = Math.min(9, Math.max(5, Math.min(width, height) * 0.075));
    const inset = bump * Math.max(...CLOUD_BUMP_RHYTHM) + CLOUD_STROKE;
    const cx = width / 2;
    const cy = height / 2;
    const rx = cx - inset;
    const ry = cy - inset;

    if (rx <= 0 || ry <= 0) {
      return '';
    }

    // Start at the top centre and run clockwise, so an arc with sweep-flag 1 bulges outwards.
    const exponent = 2 / CLOUD_EXPONENT;
    const points = [];
    const lengths = [0];

    for (let i = 0; i <= CLOUD_SAMPLES; i++) {
      const angle = -Math.PI / 2 + (i / CLOUD_SAMPLES) * Math.PI * 2;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);

      points.push([
        cx + rx * Math.sign(cos) * Math.abs(cos) ** exponent,
        cy + ry * Math.sign(sin) * Math.abs(sin) ** exponent,
      ]);

      if (i > 0) {
        const [x0, y0] = points[i - 1];
        const [x1, y1] = points[i];
        lengths.push(lengths[i - 1] + Math.hypot(x1 - x0, y1 - y0));
      }
    }

    const total = lengths[CLOUD_SAMPLES];
    const count = Math.max(8, Math.round(total / CLOUD_BUMP_SPACING));
    const step = total / count;
    const anchors = [];

    // Anchors sit half a step off the start, which centres a bump on the top of the cloud.
    for (let k = 0, j = 0; k < count; k++) {
      const target = step * (k + 0.5);

      while (j < CLOUD_SAMPLES - 1 && lengths[j + 1] < target) {
        j++;
      }

      const span = lengths[j + 1] - lengths[j] || 1;
      const ratio = (target - lengths[j]) / span;
      const [x0, y0] = points[j];
      const [x1, y1] = points[j + 1];

      anchors.push([x0 + (x1 - x0) * ratio, y0 + (y1 - y0) * ratio]);
    }

    let path = `M${round(anchors[0][0])} ${round(anchors[0][1])}`;

    for (let k = 0; k < count; k++) {
      const [x1, y1] = anchors[k];
      const [x2, y2] = anchors[(k + 1) % count];
      const halfChord = Math.hypot(x2 - x1, y2 - y1) / 2;
      const bulge = bump * CLOUD_BUMP_RHYTHM[k % CLOUD_BUMP_RHYTHM.length];
      const radius = (bulge * bulge + halfChord * halfChord) / (2 * bulge);

      path += `A${round(radius)} ${round(radius)} 0 0 1 ${round(x2)} ${round(y2)}`;
    }

    return `${path}Z`;
  }

  /* ------------------------------------------------------------------------------------------------
   * Cart requests
   * ---------------------------------------------------------------------------------------------- */

  async function cartRequest(endpoint, body) {
    const response = await fetch(`${endpoint}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest', // makes the endpoint answer a real 422 on stock errors
      },
      body: JSON.stringify(body),
    });

    let json = {};
    try {
      json = await response.json();
    } catch (error) {
      // Non-JSON error page: fall through to the generic message.
    }

    if (!response.ok) {
      throw new Error((json && (json.description || json.message)) || GENERIC_ERROR);
    }

    return json;
  }

  async function fetchCart() {
    try {
      const response = await fetch(`${routes().cartUrl || `${rootUrl()}cart`}.js`, { credentials: 'same-origin' });
      return response.ok ? await response.json() : null;
    } catch (error) {
      return null;
    }
  }

  /** Hand the fresh cart to the drawer, cart count, freebie sync and box builder the way the theme expects. */
  function publishCart(cart, sections) {
    if (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart') {
      // The cart page renders server-side only (line-item quantity changes reload it too).
      window.location.reload();
      return;
    }

    if (!cart) {
      // The change landed but the cart could not be read back: let the drawer fetch itself.
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return;
    }

    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));

    if (sections && sections[SECTION_ID]) {
      cart.sections = sections;
    }

    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));
  }

  /* ------------------------------------------------------------------------------------------------
   * Drawer-level wiring, set up once per <cart-drawer> element
   * ---------------------------------------------------------------------------------------------- */

  function highlightLine(drawer, line) {
    if (prefersReducedMotion()) {
      return;
    }

    // Drops in with a little bounce, as if the bubble had just "dropped" it into the cart.
    line.animate(
      [
        { opacity: 0, transform: 'translateY(-16px) scale(0.97)' },
        { opacity: 1, transform: 'translateY(3px) scale(1.01)', offset: 0.55 },
        { opacity: 1, transform: 'translateY(-1px) scale(1)', offset: 0.8 },
        { opacity: 1, transform: 'none' },
      ],
      { duration: 640, easing: EASE_SETTLE }
    );

    const glow = window.getComputedStyle(drawer).getPropertyValue('--cart-bubble-bg').trim();
    const wrapper = line.querySelector('.line-item__content-wrapper');

    if (glow && wrapper) {
      const previousRadius = wrapper.style.borderRadius;
      wrapper.style.borderRadius = '12px';

      const flash = wrapper.animate(
        [{ backgroundColor: glow }, { backgroundColor: glow, offset: 0.35 }, { backgroundColor: 'rgba(0, 0, 0, 0)' }],
        { duration: 1600, easing: 'ease-out' }
      );

      settle([flash], 2000).then(() => {
        wrapper.style.borderRadius = previousRadius;
      });
    }
  }

  function watchDrawer(drawer) {
    if (!drawer || watchedDrawers.has(drawer)) {
      return;
    }

    watchedDrawers.add(drawer);

    // Opening the drawer reveals the bubbles that were rendered while it was closed. Closing it ends
    // the "just added" moment, so the next plain open shows the bubbles still.
    new MutationObserver(() => {
      if (!drawer.hasAttribute('open')) {
        entrance = null;
        upgradeMoment = null;
        return;
      }

      drawer.querySelectorAll('cart-bubble.is-pending').forEach((bubble, index) => {
        bubble.reveal(OPEN_REVEAL_DELAY + index * REVEAL_STAGGER);
      });
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });

    // CartDrawer swaps its whole innerHTML, so the direct children change on every re-render.
    new MutationObserver(() => {
      if (!variantToHighlight) {
        carryUpgradeMoment(drawer);
        return;
      }

      const line = drawer.querySelector(`line-item[data-id="${variantToHighlight}"]`);

      if (line) {
        const celebration = lineCelebration;
        const variantId = variantToHighlight;

        variantToHighlight = null;
        lineCelebration = null;
        clearTimeout(highlightTimer);

        if (celebration) {
          const { tag, burst } = celebrateUpgrade(line, celebration);
          upgradeMoment = { variantId, tag, burst, until: Date.now() + UPGRADE_MOMENT_WINDOW };
        }

        highlightLine(drawer, line);
      }
    }).observe(drawer, { childList: true });
  }

  /**
   * @param {object} [celebration] - set for an upgrade: { text, animate } for celebrateUpgrade().
   */
  function expectNewLine(variantId, celebration = null) {
    variantToHighlight = String(variantId);
    lineCelebration = celebration;
    clearTimeout(highlightTimer);

    // Never let a stale highlight fire on some unrelated, much later re-render.
    highlightTimer = setTimeout(() => {
      variantToHighlight = null;
    }, 6000);
  }

  /* ------------------------------------------------------------------------------------------------
   * <cart-bubble>
   * ---------------------------------------------------------------------------------------------- */

  class CartBubble extends HTMLElement {
    connectedCallback() {
      if (!isDesignMode() && this.dismissKey && dismissedKeys.has(this.dismissKey)) {
        // Says why, both in the console and in the drawer's "Show bubble diagnostics" readout: a
        // bubble the Liquid rendered but the visitor dismissed is otherwise impossible to tell apart
        // from one that was never rendered.
        const why = '"Not now" was clicked for it. It is offered again on the next add to cart, after a reload, or on the next page.';
        const note = this.closest('line-item')?.querySelector(`[data-bubble-debug="${this.dataset.type}"]`);

        if (note) {
          note.textContent += ` But hidden: ${why}`;
        }

        console.info(`[cart-bubble] ${this.dataset.type} bubble hidden: ${why}`);

        // Runs while CartDrawer is still inserting the new markup, i.e. before paint: no flash.
        this.remove();
        return;
      }

      this.drawer = this.closest('cart-drawer');
      this.cloud = this.querySelector('.cart-bubble__cloud');
      this.shape = this.querySelector('.cart-bubble__shape path');

      watchDrawer(this.drawer);

      this._onClick = this._onClick.bind(this);
      this.addEventListener('click', this._onClick);

      if (this.cloud && 'ResizeObserver' in window) {
        // The first notification arrives after layout and before paint, so the outline is in
        // place by the first frame. Later ones follow the drawer's width across breakpoints.
        this._resizeObserver = new ResizeObserver(() => this.drawCloud());
        this._resizeObserver.observe(this.cloud);
      } else {
        this.drawCloud();
      }

      if (!this.shouldAnimate || !wantsEntrance(this.key)) {
        this.classList.add('is-revealed');
        // A re-render for the same add (the freebie sync does one) shows the bubble still, but keeps
        // its nudges going: it is still the "just added" moment.
        this.startIdle(Boolean(entrance && entrance.played.has(this.key)));
        return;
      }

      this.classList.add('is-pending');

      if (this.drawer && this.drawer.hasAttribute('open')) {
        this.reveal(LIVE_REVEAL_DELAY);
      }
      // Otherwise watchDrawer() reveals it when the drawer opens.
    }

    disconnectedCallback() {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
      }

      if (this._onClick) {
        this.removeEventListener('click', this._onClick);
      }

      clearTimeout(this._nudgeTimer);
    }

    get key() {
      return this.dataset.key || '';
    }

    get dismissKey() {
      return this.dataset.dismissKey || '';
    }

    get shouldAnimate() {
      return this.dataset.animate !== 'false' && !prefersReducedMotion();
    }

    drawCloud() {
      if (!this.cloud || !this.shape) {
        return;
      }

      // Layout size, not getBoundingClientRect: the reveal animation scales the cloud.
      const width = this.cloud.offsetWidth;
      const height = this.cloud.offsetHeight;

      if (!width || !height || (width === this._drawnWidth && height === this._drawnHeight)) {
        return;
      }

      this._drawnWidth = width;
      this._drawnHeight = height;

      // A designed outline (data-fixed-shape) ships in the markup and only stretches with the box.
      if (!this.cloud.hasAttribute('data-fixed-shape')) {
        const path = cloudPath(width, height);

        if (!path) {
          return;
        }

        this.shape.ownerSVGElement.setAttribute('viewBox', `0 0 ${width} ${height}`);
        this.shape.setAttribute('d', path);
        this.cloud.classList.add('is-drawn');
      }

      if (this.dataset.trail === 'art') {
        this.alignTrail();
      }
    }

    /**
     * Seats the designed trail (data-trail="art") on the cloud. The outline is stretched to the
     * content, so where its lobes sit moves with the bubble's size; a trail pinned to the box would
     * drift off them. Measure the drawn outline instead:
     *
     *  - cross-sell: the trail hangs under the bottom-left lobe. Find the outline above the trail's
     *    largest puff and hand the CSS that distance (--cart-bubble-edge); it hangs the trail a
     *    fixed gap below.
     *  - upsell: the trail drops onto the bulge (the [data-trail-anchor] path). Find the bulge's
     *    highest point and place the trail so its largest puff sits just above it
     *    (--cart-bubble-trail-left / -top).
     *
     * Either way, the meeting point is the transform origin the cloud blooms out of.
     */
    alignTrail() {
      const trail = this.querySelector('.cart-bubble__trail');
      const anchorX = parseFloat(trail && trail.dataset.anchorX);

      if (!trail || !Number.isFinite(anchorX) || typeof this.shape.getTotalLength !== 'function') {
        return;
      }

      // The SVG fills the cloud's padding box; path coordinates are in viewBox units.
      const width = this.cloud.clientWidth;
      const height = this.cloud.clientHeight;
      const viewBox = this.shape.ownerSVGElement.viewBox.baseVal;
      const scaleX = viewBox && viewBox.width ? width / viewBox.width : 1;
      const scaleY = viewBox && viewBox.height ? height / viewBox.height : 1;
      const anchor = this.shape.ownerSVGElement.querySelector('[data-trail-anchor]');

      if (anchor) {
        const anchorY = parseFloat(trail.dataset.anchorY) || 0;
        const length = anchor.getTotalLength();
        const points = [];

        // Sample the bulge in pixels inside the cloud's border box.
        for (let i = 0; i <= 200; i++) {
          const point = anchor.getPointAtLength((length * i) / 200);
          points.push([point.x * scaleX + this.cloud.clientLeft, point.y * scaleY + this.cloud.clientTop]);
        }

        // The bulge's crest is a short flat top: use the middle of it, not whichever point is lowest.
        const peakY = Math.min(...points.map(([, y]) => y));
        const crest = points.filter(([, y]) => y - peakY < 0.75).map(([x]) => x);
        const peakX = (Math.min(...crest) + Math.max(...crest)) / 2;

        // Measured in the Figma "Cart mobile" frame: the largest puff's outline ends ~9.4px above the
        // bulge's outline, with its centre ~9.7px left of the crest's centre. data-anchor-x/-y mark
        // that puff's centre and bottom; `gap` is calibrated against a pixel measurement of the
        // rendered bubble so the visible space comes out at Figma's 9.4px.
        const gap = 10;
        const shiftX = -9.7;

        this.style.setProperty('--cart-bubble-trail-left', `${round(this.cloud.offsetLeft + peakX + shiftX - anchorX)}px`);
        this.style.setProperty('--cart-bubble-trail-top', `${round(this.cloud.offsetTop + peakY - anchorY - gap)}px`);
        this.style.setProperty('--cart-bubble-origin-x', `${round(peakX)}px`);
        this.style.setProperty('--cart-bubble-origin-y', `${round(peakY)}px`);
        this.clearImage(trail);
        return;
      }

      const x = trail.offsetLeft + anchorX - this.cloud.offsetLeft - this.cloud.clientLeft;
      const total = this.shape.getTotalLength();
      const samples = 400;
      let bottom = -Infinity;

      for (let i = 0; i <= samples; i++) {
        const point = this.shape.getPointAtLength((total * i) / samples);

        if (Math.abs(point.x * scaleX - x) < 4) {
          bottom = Math.max(bottom, point.y * scaleY);
        }
      }

      if (!Number.isFinite(bottom)) {
        return;
      }

      const edge = this.cloud.offsetHeight - this.cloud.clientTop - bottom;

      this.style.setProperty('--cart-bubble-edge', `${round(edge)}px`);
      this.style.setProperty('--cart-bubble-origin-x', `${round(x + this.cloud.clientLeft)}px`);
    }

    /**
     * The upsell tucks up under its line, which only works while the line's text column is taller
     * than its product image (a two-line title). When the image is the taller of the two, the trail
     * would run into it; push the bubble down (--cart-bubble-clearance) by exactly the shortfall,
     * keeping a 6px gap. Vertical positions are unaffected by the drawer's slide-in transform.
     */
    clearImage(trail) {
      const line = this.closest('line-item');
      const image = line && line.querySelector('.line-item__image-wrapper');

      if (!image) {
        return;
      }

      const current = parseFloat(this.style.getPropertyValue('--cart-bubble-clearance')) || 0;
      const shortfall = image.getBoundingClientRect().bottom + 6 - trail.getBoundingClientRect().top;
      const next = Math.max(0, current + shortfall);

      if (Math.abs(next - current) > 0.5) {
        this.style.setProperty('--cart-bubble-clearance', `${round(next)}px`);
      }
    }

    /** Dots pop from the line item towards the cloud, then the cloud blooms out of the last dot. */
    reveal(delay = 0) {
      if (!this.classList.contains('is-pending')) {
        return;
      }

      this.classList.remove('is-pending');
      this.classList.add('is-revealed');

      if (!this.shouldAnimate) {
        return;
      }

      // Only opacity and transform are animated, so it all runs on the compositor and stays smooth
      // while the drawer itself is still sliding in. fill: 'backwards' holds each first keyframe
      // through its delay, so nothing flashes in early.
      const animations = [];
      const dots = Array.from(this.querySelectorAll('.cart-bubble__dot')); // line item -> cloud

      // 1. The puffs pop out of the line item one after another, each overshooting and settling.
      dots.forEach((dot, index) => {
        animations.push(
          dot.animate(
            [
              { opacity: 0, transform: 'scale(0) rotate(-25deg)' },
              { opacity: 1, transform: 'scale(1.45) rotate(8deg)', offset: 0.45 },
              { opacity: 1, transform: 'scale(0.85) rotate(-3deg)', offset: 0.7 },
              { opacity: 1, transform: 'scale(1.06)', offset: 0.86 },
              { opacity: 1, transform: 'none' },
            ],
            { duration: 480, delay: delay + index * DOT_STAGGER, easing: EASE_SETTLE, fill: 'backwards' }
          )
        );
      });

      // 2. The cloud blooms out of the last puff and lands like jelly: stretch, squash, settle.
      const cloudDelay = delay + dots.length * DOT_STAGGER - 40;

      if (this.cloud) {
        animations.push(
          this.cloud.animate(
            [
              { opacity: 0, transform: 'scale(0.08) rotate(-9deg)' },
              { opacity: 1, transform: 'scale(1.07, 1.01) rotate(2deg)', offset: 0.42 },
              { opacity: 1, transform: 'scale(0.95, 1.05) rotate(-1deg)', offset: 0.6 },
              { opacity: 1, transform: 'scale(1.025, 0.98) rotate(0.4deg)', offset: 0.76 },
              { opacity: 1, transform: 'scale(0.995, 1.006)', offset: 0.9 },
              { opacity: 1, transform: 'none' },
            ],
            { duration: 960, delay: cloudDelay, easing: EASE_SETTLE, fill: 'backwards' }
          )
        );
      }

      // 3. The content pops in piece by piece once the cloud has mostly formed.
      this.revealContent(cloudDelay + 360).forEach((animation) => animations.push(animation));

      // 4. Last, the "Not now" link drops in under the cloud.
      const dismiss = this.querySelector('.cart-bubble__dismiss');

      if (dismiss) {
        animations.push(
          dismiss.animate(
            [
              { opacity: 0, transform: 'translateY(-8px)' },
              { opacity: 1, transform: 'translateY(2px)', offset: 0.7 },
              { opacity: 1, transform: 'none' },
            ],
            { duration: 420, delay: cloudDelay + 820, easing: EASE_SETTLE, fill: 'backwards' }
          )
        );
      }

      settle(animations, delay + 3000).then(() => this.startIdle(true));
    }

    /** Pops each piece of content in turn, each with its own character. Returns the animations. */
    revealContent(start) {
      const pieces = [
        // [selector, keyframes] in the order they should appear
        ['.cart-bubble__image-frame, .cart-bubble__content--cross-sell > .cart-bubble__media', [
          { opacity: 0, transform: 'scale(0.55) rotate(-10deg)' },
          { opacity: 1, transform: 'scale(1.1) rotate(4deg)', offset: 0.5 },
          { opacity: 1, transform: 'scale(0.97) rotate(-1.5deg)', offset: 0.75 },
          { opacity: 1, transform: 'none' },
        ]],
        ['.cart-bubble__usp', [
          { opacity: 0, transform: 'translateY(-8px) scale(0.8)' },
          { opacity: 1, transform: 'translateY(2px) scale(1.06)', offset: 0.6 },
          { opacity: 1, transform: 'none' },
        ]],
        // The yellow label is "drawn in" from the left, like a highlighter stroke.
        ['.cart-bubble__eyebrow', [
          { opacity: 0, transform: 'translateX(-10px)', clipPath: 'inset(0 100% 0 0)' },
          { opacity: 1, transform: 'translateX(2px)', clipPath: 'inset(0 0 0 0)', offset: 0.7 },
          { opacity: 1, transform: 'none', clipPath: 'inset(0 0 0 0)' },
        ]],
        ['.cart-bubble__caption, .cart-bubble__title', [
          { opacity: 0, transform: 'translateY(8px)' },
          { opacity: 1, transform: 'translateY(-2px)', offset: 0.65 },
          { opacity: 1, transform: 'none' },
        ]],
        ['.cart-bubble__saving', [
          { opacity: 0, transform: 'translateY(8px)', clipPath: 'inset(0 100% 0 0)' },
          { opacity: 1, transform: 'translateY(-1px)', clipPath: 'inset(0 0 0 0)', offset: 0.7 },
          { opacity: 1, transform: 'none', clipPath: 'inset(0 0 0 0)' },
        ]],
        ['.cart-bubble__price', [
          { opacity: 0, transform: 'translateY(8px) scale(0.9)' },
          { opacity: 1, transform: 'translateY(-2px) scale(1.04)', offset: 0.65 },
          { opacity: 1, transform: 'none' },
        ]],
        // The call to action arrives last and biggest.
        ['.cart-bubble__button', [
          { opacity: 0, transform: 'scale(0.4)' },
          { opacity: 1, transform: 'scale(1.18)', offset: 0.5 },
          { opacity: 1, transform: 'scale(0.93)', offset: 0.72 },
          { opacity: 1, transform: 'scale(1.03)', offset: 0.88 },
          { opacity: 1, transform: 'none' },
        ]],
      ];

      const animations = [];
      let step = 0;

      pieces.forEach(([selector, keyframes]) => {
        const element = this.querySelector(selector);

        if (!element) {
          return;
        }

        animations.push(
          element.animate(keyframes, {
            duration: selector === '.cart-bubble__button' ? 620 : 480,
            delay: start + step * CONTENT_STAGGER,
            easing: EASE_SETTLE,
            fill: 'backwards',
          })
        );
        step += 1;
      });

      return animations;
    }

    /**
     * Once in place, the bubble keeps "thinking": a gentle float with the puffs bobbing out of step
     * (CSS, .is-idle), and a few playful wiggles of its button to draw the eye. Each wiggle waits
     * until the drawer is open, the tab visible, and the visitor isn't hovering, focused or busy.
     */
    startIdle(withNudges) {
      if (!this.shouldAnimate || !this.isConnected) {
        return;
      }

      this.classList.add('is-idle');

      if (!withNudges) {
        return;
      }

      clearTimeout(this._nudgeTimer);

      const schedule = (wait) => {
        this._nudgeTimer = setTimeout(() => {
          const nudges = nudgeCounts.get(this.key) || 0;

          if (!this.isConnected || nudges >= NUDGE_TIMES) {
            return;
          }

          const canNudge =
            this.drawer && this.drawer.hasAttribute('open') && document.visibilityState === 'visible' &&
            !this._busy && !this.matches(':hover') && !this.contains(document.activeElement);

          if (canNudge) {
            this.nudge();
            nudgeCounts.set(this.key, nudges + 1);
          }

          schedule(NUDGE_EVERY);
        }, wait);
      };

      schedule(NUDGE_FIRST);
    }

    /** A quick wiggle of the button, with the puffs rippling up towards the cloud. */
    nudge() {
      const button = this.querySelector('.cart-bubble__button');

      if (button) {
        button.animate(
          [
            { transform: 'none' },
            { transform: 'scale(1.08) rotate(-5deg)', offset: 0.18 },
            { transform: 'scale(1.08) rotate(4deg)', offset: 0.36 },
            { transform: 'scale(1.05) rotate(-3deg)', offset: 0.54 },
            { transform: 'scale(1.02) rotate(1.5deg)', offset: 0.72 },
            { transform: 'none' },
          ],
          { duration: 720, easing: 'ease-in-out' }
        );
      }

      this.querySelectorAll('.cart-bubble__dot').forEach((dot, index) => {
        dot.animate(
          [{ transform: 'none' }, { transform: 'scale(1.3)', offset: 0.4 }, { transform: 'none' }],
          { duration: 420, delay: index * 90, easing: EASE_POP }
        );
      });
    }

    /**
     * Chevrons that rise out of the UPGRADE button, one after another, as the bubble whooshes away.
     * Returns the animations.
     */
    riseFromButton() {
      const button = this.querySelector('.cart-bubble__button');
      const body = this.querySelector('.cart-bubble__body');

      if (!button || !body) {
        return [];
      }

      const from = button.getBoundingClientRect();
      const origin = body.getBoundingClientRect();
      const animations = [];

      [0, 1, 2].forEach((index) => {
        const chevron = document.createElement('span');
        chevron.className = 'cart-bubble__rise';
        chevron.setAttribute('aria-hidden', 'true');
        // A double chevron (⌃⌃) reads as "up" even at a glance; the middle one leads the other two.
        chevron.style.left = `${from.left - origin.left + from.width / 2 + (index - 1) * 20}px`;
        chevron.style.top = `${from.top - origin.top - 6}px`;
        chevron.append(
          svg(
            '<path d="M12 22l8-8 8 8M12 30l8-8 8 8" fill="none" stroke="currentColor" stroke-width="3.6" stroke-linecap="round" stroke-linejoin="round"/>',
            '8 8 24 24'
          )
        );
        body.append(chevron);

        const animation = chevron.animate(
          [
            { opacity: 0, transform: 'translateY(10px) scale(0.5)' },
            { opacity: 1, transform: 'translateY(-18px) scale(1.1)', offset: 0.35 },
            { opacity: 0, transform: 'translateY(-96px) scale(0.9)' },
          ],
          { duration: 760, delay: index === 1 ? 0 : 90, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)', fill: 'both' }
        );

        animation.finished.catch(() => {}).then(() => chevron.remove());
        animations.push(animation);
      });

      return animations;
    }

    /**
     * The reverse of reveal(), with a playful "pop", then the space it held closes.
     * { upgrade: true }: the cloud whooshes UP and away, chevrons rising out of the button.
     */
    async conceal({ upgrade = false } = {}) {
      this.classList.add('is-leaving');
      clearTimeout(this._nudgeTimer);

      if (!this.shouldAnimate) {
        return;
      }

      const animations = [];
      const content = this.querySelector('.cart-bubble__content');
      const dismiss = this.querySelector('.cart-bubble__dismiss');
      const dots = Array.from(this.querySelectorAll('.cart-bubble__dot')).reverse(); // cloud -> line item

      if (content) {
        animations.push(
          content.animate(
            [
              { opacity: 1, transform: 'none' },
              { opacity: 0, transform: 'scale(0.92)' },
            ],
            { duration: 170, easing: 'ease-in', fill: 'forwards' }
          )
        );
      }

      if (dismiss) {
        animations.push(dismiss.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 140, fill: 'forwards' }));
      }

      if (upgrade && this.cloud) {
        // Upgrade: a little crouch, then the cloud launches upwards, stretching as it goes.
        this.riseFromButton().forEach((animation) => animations.push(animation));
        animations.push(
          this.cloud.animate(
            [
              { opacity: 1, transform: 'none' },
              { opacity: 1, transform: 'translateY(5px) scale(1.04, 0.95)', offset: 0.18 },
              { opacity: 1, transform: 'translateY(-14px) scale(0.97, 1.05)', offset: 0.45 },
              { opacity: 0, transform: 'translateY(-60px) scale(0.85, 1.1)' },
            ],
            { duration: 640, delay: 120, easing: EASE_IN, fill: 'forwards' }
          )
        );
      } else if (this.cloud) {
        // The cloud puffs up, squishes, then shrinks away into its trail with a little spin.
        animations.push(
          this.cloud.animate(
            [
              { opacity: 1, transform: 'none' },
              { opacity: 1, transform: 'scale(1.07, 0.95)', offset: 0.2 },
              { opacity: 1, transform: 'scale(0.94, 1.04)', offset: 0.38 },
              { opacity: 0, transform: 'scale(0.06) rotate(10deg)' },
            ],
            { duration: 540, delay: 60, easing: EASE_IN, fill: 'forwards' }
          )
        );
      }

      // Then the puffs pop one by one back towards the line item.
      dots.forEach((dot, index) => {
        animations.push(
          dot.animate(
            [
              { opacity: 1, transform: 'none' },
              { opacity: 1, transform: 'scale(1.35)', offset: 0.35 },
              { opacity: 0, transform: 'scale(0)' },
            ],
            { duration: 260, delay: 420 + index * 75, easing: EASE_IN, fill: 'forwards' }
          )
        );
      });

      await settle(animations, 1100);
      await this.collapse(this);
    }

    /** Animates an element's height (and vertical padding) down to nothing. */
    async collapse(element) {
      if (!this.shouldAnimate || !element.isConnected) {
        return;
      }

      // Height, padding and top margin all go to zero together, so the content below glides up by the
      // exact space this element held (the upsell's negative top margin included).
      const style = window.getComputedStyle(element);
      element.style.overflow = 'hidden';

      const animation = element.animate(
        [
          {
            height: `${element.offsetHeight}px`,
            paddingTop: style.paddingTop,
            paddingBottom: style.paddingBottom,
            marginTop: style.marginTop,
            opacity: 1,
          },
          { height: '0px', paddingTop: '0px', paddingBottom: '0px', marginTop: '0px', opacity: 0 },
        ],
        { duration: 340, easing: 'cubic-bezier(0.65, 0, 0.35, 1)', fill: 'forwards' }
      );

      await settle([animation], 700);
    }

    _onClick(event) {
      const trigger = event.target.closest('[data-bubble-action]');

      if (!trigger || !this.contains(trigger)) {
        return;
      }

      event.preventDefault();

      switch (trigger.dataset.bubbleAction) {
        case 'add':
          this.addOffer(trigger);
          break;
        case 'upgrade':
          this.upgrade(trigger);
          break;
        case 'dismiss':
          this.dismiss();
          break;
      }
    }

    /** The /cart/add item, carrying the bubble's order label when the setting has one. */
    cartItem(id, quantity) {
      const item = { id, quantity };
      const orderText = (this.dataset.orderText || '').trim();
      if (orderText) {
        item.properties = { [ORDER_PROPERTY]: orderText };
      }
      return item;
    }

    async addOffer(button) {
      if (!this.begin(button)) {
        return;
      }

      try {
        const variantId = Number(this.dataset.variantId);
        const added = await cartRequest(routes().cartAddUrl || `${rootUrl()}cart/add`, {
          items: [this.cartItem(variantId, 1)],
          sections: [SECTION_ID],
        });

        armEntrance(false);
        expectNewLine(variantId);

        const [cart] = await Promise.all([fetchCart(), this.conceal()]);
        publishCart(cart, added.sections);
        // Only now (see announceAdded): the freebie sync it starts must render after this refresh.
        this.announceAdded(added);
      } catch (error) {
        this.fail(button, error);
      }
    }

    async upgrade(button) {
      if (!this.begin(button)) {
        return;
      }

      try {
        const variantId = Number(this.dataset.variantId);
        const quantity = Math.max(1, parseInt(this.dataset.quantity, 10) || 1);

        // Add first. If it fails, the original line is untouched and the error shows in the bubble.
        const added = await cartRequest(routes().cartAddUrl || `${rootUrl()}cart/add`, {
          items: [this.cartItem(variantId, quantity)],
        });

        let sections = added.sections;

        try {
          const changed = await cartRequest(routes().cartChangeUrl || `${rootUrl()}cart/change`, {
            id: this.dataset.lineKey,
            quantity: 0,
            sections: [SECTION_ID],
          });
          sections = changed.sections;
        } catch (error) {
          // The upgrade itself landed; only the old line stayed. The refresh shows the cart as it is.
        }

        armEntrance(false);

        // The upgraded product gets the arrow-up celebration when it lands (data-celebrate holds its
        // label; absent when the "Upgrade celebration" setting is off).
        const celebrate = this.dataset.celebrate;
        expectNewLine(variantId, celebrate ? { text: celebrate, animate: this.shouldAnimate } : null);

        const line = this.closest('line-item');
        const [cart] = await Promise.all([
          fetchCart(),
          this.conceal({ upgrade: Boolean(celebrate) }).then(() => (line ? this.collapse(line) : null)),
        ]);

        publishCart(cart, sections);
        this.announceAdded(added);
      } catch (error) {
        this.fail(button, error);
      }
    }

    async dismiss() {
      if (this._busy) {
        return;
      }

      this._busy = true;

      if (!isDesignMode()) {
        dismissedKeys.add(this.dismissKey);
      }

      await this.conceal();
      this.remove();
    }

    /**
     * Same contract as the theme's ProductForm, minus quickbuy:upsell (see the file header).
     * Dispatched AFTER publishCart(): variant:added starts the theme's freebie sync (theme.js
     * syncFreebies), which adds unlocked freebies and re-renders the drawer. Announced earlier, that
     * sync could finish first and this flow's older cart snapshot would then overwrite the drawer,
     * hiding freebies that are in the cart. (The entrance is armed with armEntrance() up front.)
     */
    announceAdded(response) {
      const variant = response && Array.isArray(response.items) ? response.items[0] : response;

      // cartBubble marks it as an add from inside the drawer (see armEntrance).
      document.documentElement.dispatchEvent(
        new CustomEvent('variant:added', { bubbles: true, detail: { variant, cartBubble: true } })
      );
    }

    begin(button) {
      if (this._busy) {
        return false;
      }

      this._busy = true;
      this.clearError();
      button.setAttribute('aria-busy', 'true'); // loader-button swaps the label for its spinner
      this.querySelectorAll('button').forEach((element) => {
        element.disabled = true;
      });

      return true;
    }

    fail(button, error) {
      this._busy = false;
      button.removeAttribute('aria-busy');
      this.querySelectorAll('button').forEach((element) => {
        element.disabled = false;
      });

      const message = this.querySelector('.cart-bubble__error');

      if (message) {
        message.textContent = (error && error.message) || GENERIC_ERROR;
        message.hidden = false;
      }
    }

    clearError() {
      const message = this.querySelector('.cart-bubble__error');

      if (message) {
        message.hidden = true;
        message.textContent = '';
      }
    }
  }

  window.customElements.define('cart-bubble', CartBubble);
  document.querySelectorAll('cart-drawer').forEach(watchDrawer);
})();
